/**
 * @fileoverview CRUD Modal scenario detector for CopilotBar.
 *
 * Handles CopilotBar configuration when entity CRUD operations are
 * performed within a modal overlay (e.g., quick create from related entities).
 *
 * @module CopilotBar/scenarios/crud/crudModal
 */

import { COPILOT_BAR_SCENARIOS } from '../constants';

import { buildCrudConfig, extractCrudContext } from './crudHelpers';

/**
 * Scenario detector for CRUD operations in modal overlays.
 *
 * This scenario is activated when:
 * - An entity modal is open (`entityModalState.isOpen === true`)
 * - The modal has an active entity (`entityModalState.active` is truthy)
 *
 * When active, the CopilotBar is displayed within the modal overlay with:
 * - Full width (100%)
 * - Not pinned to the right (centered in modal)
 *
 * @type {Object}
 * @property {string} scenario - The scenario type identifier (CRUD_MODAL)
 * @property {Function} condition - Evaluates if a CRUD modal is currently open
 * @property {Function} buildConfig - Builds the CopilotBar configuration for modal context
 *
 * @example
 * // Condition evaluation
 * crudModalScenario.condition({ entityModalState: { isOpen: true, active: 'accounts' } });
 * // Returns: true
 */
export const crudModalScenario = {
    buildConfig: ({ dispatch, getEntityCrudState, setCopilotBarProps, state }) => {
        const { entityCrudState } = state;
        const context = extractCrudContext(
            entityCrudState.activeCrudName,
            entityCrudState.entityCrud,
        );

        if (!context) return null;

        return buildCrudConfig({
            context,
            dispatch,
            getEntityCrudState,
            ignoreCopilotSidebarInset: true,
            pinnedToRight: false,
            setCopilotBarProps,
            width: '100%',
        });
    },
    condition: ({ entityModalState }) => {
        const entityModalActive = entityModalState.active;
        return entityModalState.isOpen && entityModalActive;
    },
    scenario: COPILOT_BAR_SCENARIOS.CRUD_MODAL,
};
